import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import {
    Avatar,
    Badge,
    Box,
    Button,
    Flex,
    HStack,
    IconButton,
    SimpleGrid,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    VStack,
    useColorModeValue,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import React from 'react';
import { useParams } from 'react-router-dom';
import PageCard from '../../../components/page-card';
import ErrorState from '../../../components/states/ErrorState';
import { translate } from '../../../i18n';
import { useNavigateToUserDetails } from '../../_shared/calendar/hooks/useNavigateToUserDetails';
import { tableHeaderTextProps, tableProps, tableRowProps } from '../../../theme/constants';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { CouponDetailDto, CouponUserAssignment, useGetCoupon } from '../hooks/useGetCoupon';
import { useRevokeCoupon } from '../hooks/useRevokeCoupon';
import AssignCouponModal from './AssignCouponModal';

const PAYABLE_TYPE_LABELS: Record<string, string> = {
    COURT_BOOKING: translate('coupon.applicable_court_booking'),
    GAME_JOINING: translate('coupon.applicable_game_joining'),
    EVENT_JOINING: translate('coupon.applicable_event_joining'),
    COACHING_SESSION: translate('coupon.applicable_coaching_session'),
};

export default function CouponDetailPage() {
    const { couponId } = useParams<{ couponId: string }>();
    const toast = useToast();
    const navigateToUserDetails = useNavigateToUserDetails();
    const { selectedOrganization } = useSelectedOrganization();

    const { data: coupon, isLoading, isError, refetch } = useGetCoupon(couponId);
    const revokeCoupon = useRevokeCoupon(couponId || '', selectedOrganization?.id || '');

    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

    const {
        isOpen: isAssignOpen,
        onOpen: onAssignOpen,
        onClose: onAssignClose,
    } = useDisclosure();

    const handleRevoke = async (user: CouponUserAssignment) => {
        if (
            window.confirm(
                `${translate('coupon.confirm_revoke_user')} ${user.firstName} ${user.lastName}?`,
            )
        ) {
            try {
                await revokeCoupon.mutateAsync(user.userId);
                toast({
                    title: translate('coupon.user_revoked_successfully'),
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
            } catch (error) {
                toast({
                    title: translate('coupon.revoke_failed'),
                    description: error instanceof Error ? error.message : undefined,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            }
        }
    };

    // Only a genuinely in-flight read spins. The query is `enabled: !!couponId`,
    // and React Query v4 reports a DISABLED query as `isLoading: true` with
    // `fetchStatus: 'idle'`, so a route reached without a `couponId` would
    // otherwise spin forever rather than fall through to the branch below.
    const couponPending = Boolean(couponId) && !isError && isLoading;
    if (!coupon && couponPending) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Spinner size="lg" />
            </Box>
        );
    }

    // `GET coupons/admin/detail/:id` carries `AdminAuthGuard` +
    // `hasOrganizationAdminAccess`, so an operator who is a member of the
    // selected org without being an admin of it is REFUSED with no outage at
    // all — on top of a deleted coupon (404) and a 500. All three used to land
    // in a red error Alert whose text was `coupon.loading`, i.e. "Loading…" /
    // "Chargement…". An error that says it is loading is worse than no message:
    // it tells the operator to wait for something that will never arrive, and
    // it is the same glyph for "you may not see this" as for "this is gone".
    // `common.unexpected_error` exists in both locales (`src/i18n/{en,fr}.ts`
    // cannot take a new key — they are inside the open-PR fence, R96-7).
    // Gated on having NOTHING to show rather than on `isError` alone: React
    // Query keeps `coupon` when a BACKGROUND refetch fails and raises `isError`
    // beside it, and blanking a correct page because a refresh failed is the
    // mirror of this defect (R96-3).
    if (!coupon) {
        return (
            <ErrorState
                message={translate('common.unexpected_error')}
                onRetry={couponId ? () => { refetch(); } : undefined}
            />
        );
    }

    return (
        <VStack spacing={6} align="stretch">
            {/* Coupon Details Card */}
            <PageCard
                showBackButton
                title={translate('coupon.coupon_details')}
            >
                <CouponInformation coupon={coupon} onAssign={onAssignOpen} />
            </PageCard>

            {/* Assigned Users Card */}
            <PageCard
                title={`${translate('coupon.assigned_users')} (${coupon.userCoupons?.length || 0})`}
                rightHeader={
                    <Button
                        leftIcon={<AddIcon />}
                        size="sm"
                        colorScheme="brand"
                        variant="outline"
                        onClick={onAssignOpen}
                    >
                        {translate('coupon.add_users')}
                    </Button>
                }
            >
                {!coupon.userCoupons || coupon.userCoupons.length === 0 ? (
                    <Flex
                        px="25px"
                        py="50px"
                        justifyContent="center"
                        flexDirection="column"
                        align="center"
                    >
                        <Text color="gray.500" fontSize="lg" mb={4}>
                            {translate('coupon.no_assigned_users')}
                        </Text>
                        <Button colorScheme="brand" onClick={onAssignOpen}>
                            {translate('coupon.add_first_users')}
                        </Button>
                    </Flex>
                ) : (
                    <Box>
                        <Table {...tableProps}>
                            <Thead>
                                <Tr>
                                    <Th borderColor={borderColor}>
                                        <Text {...tableHeaderTextProps}>
                                            {translate('coupon.user')}
                                        </Text>
                                    </Th>
                                    <Th borderColor={borderColor}>
                                        <Text {...tableHeaderTextProps}>
                                            {translate('coupon.email')}
                                        </Text>
                                    </Th>
                                    <Th borderColor={borderColor}>
                                        <Text {...tableHeaderTextProps}>
                                            {translate('coupon.usage')}
                                        </Text>
                                    </Th>
                                    <Th borderColor={borderColor}>
                                        <Text {...tableHeaderTextProps}>
                                            {translate('coupon.assigned_at')}
                                        </Text>
                                    </Th>
                                    <Th borderColor={borderColor}>
                                        <Text {...tableHeaderTextProps}>
                                            {translate('coupon.actions')}
                                        </Text>
                                    </Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {coupon.userCoupons.map((uc) => {
                                    const fullyUsed = uc.usageCount >= uc.maxUsagePerUser;
                                    return (
                                        <Tr
                                            key={uc.id}
                                            {...tableRowProps}
                                            onClick={() => navigateToUserDetails?.(uc.userId)}
                                        >
                                            <Td borderColor="transparent">
                                                <Flex align="center" gap={3}>
                                                    <Avatar
                                                        size="sm"
                                                        name={`${uc.firstName} ${uc.lastName}`}
                                                    />
                                                    <Text color={textColor} fontSize="sm" fontWeight="700">
                                                        {uc.firstName} {uc.lastName}
                                                    </Text>
                                                </Flex>
                                            </Td>
                                            <Td borderColor="transparent">
                                                <Text color={textColor} fontSize="sm">
                                                    {uc.email}
                                                </Text>
                                            </Td>
                                            <Td borderColor="transparent">
                                                {fullyUsed ? (
                                                    <Badge colorScheme="red">{translate('coupon.fully_used')}</Badge>
                                                ) : (
                                                    <Text color={textColor} fontSize="sm">
                                                        {uc.usageCount} / {uc.maxUsagePerUser}{' '}
                                                        <Text as="span" color="gray.400">
                                                            ({uc.maxUsagePerUser - uc.usageCount} {translate('coupon.uses_left')})
                                                        </Text>
                                                    </Text>
                                                )}
                                            </Td>
                                            <Td borderColor="transparent">
                                                <Text color={textColor} fontSize="sm">
                                                    {uc.assignedAt ? new Date(uc.assignedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                                                </Text>
                                            </Td>
                                            <Td borderColor="transparent" onClick={(e) => e.stopPropagation()}>
                                                <IconButton
                                                    aria-label={translate('coupon.revoke')}
                                                    icon={<DeleteIcon />}
                                                    size="sm"
                                                    colorScheme="red"
                                                    variant="ghost"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRevoke(uc);
                                                    }}
                                                />
                                            </Td>
                                        </Tr>
                                    );
                                })}
                            </Tbody>
                        </Table>
                    </Box>
                )}
            </PageCard>

            {coupon && (
                <AssignCouponModal
                    couponId={coupon.id}
                    couponName={coupon.name}
                    isOpen={isAssignOpen}
                    onClose={onAssignClose}
                />
            )}
        </VStack>
    );
}

// ─── Coupon Information Component ─────────────────────────────────

interface CouponInformationProps {
    coupon: CouponDetailDto;
    onAssign: () => void;
}

const CouponInformation: React.FC<CouponInformationProps> = ({ coupon, onAssign }) => {
    const isExpired = new Date(coupon.expiresAt) < new Date();

    const getDiscountBadge = () => {
        const label =
            coupon.discountType === 'PERCENTAGE'
                ? `${coupon.discountValue}%`
                : `${coupon.discountValue} ${(coupon.currency || '').toUpperCase()}`;
        const color = coupon.discountType === 'PERCENTAGE' ? 'purple' : 'blue';
        return <Badge colorScheme={color}>-{label}</Badge>;
    };

    const getStatusBadge = () => {
        if (isExpired) {
            return <Badge colorScheme="red">{translate('coupon.expired')}</Badge>;
        }
        return <Badge colorScheme="green">{translate('userMembership.active')}</Badge>;
    };

    return (
        <VStack spacing={4} align="stretch">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} padding={6}>
                <VStack align="start" spacing={3}>
                    <InfoRow
                        label={translate('coupon.name')}
                        value={coupon.name}
                    />
                    <HStack>
                        <Text fontWeight="bold" minWidth="120px">{translate('coupon.discount')}:</Text>
                        {getDiscountBadge()}
                    </HStack>
                    <HStack>
                        <Text fontWeight="bold" minWidth="120px">{translate('coupon.applicable_to')}:</Text>
                        <HStack spacing={1} wrap="wrap">
                            {coupon.applicableTo.map((type: string) => (
                                <Badge key={type} colorScheme="gray" fontSize="xs">
                                    {PAYABLE_TYPE_LABELS[type] || type}
                                </Badge>
                            ))}
                        </HStack>
                    </HStack>
                    <InfoRow
                        label={translate('coupon.expires_at')}
                        value={new Date(coupon.expiresAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        valueColor={isExpired ? 'red.500' : undefined}
                    />
                </VStack>

                <VStack align="start" spacing={3}>
                    <HStack>
                        <Text fontWeight="bold" minWidth="120px">{translate('game.status')}:</Text>
                        {getStatusBadge()}
                    </HStack>
                    <InfoRow
                        label={translate('coupon.max_uses')}
                        value={`${coupon.maxUsagePerUser} ${translate('coupon.max_usage_per_user').toLowerCase()}`}
                    />
                    <InfoRow
                        label={translate('coupon.used_attributed')}
                        value={`${coupon.totalUsageCount ?? 0} / ${coupon.assignedCount ?? 0}`}
                    />
                    <HStack pt={2}>
                        <Button
                            size="sm"
                            colorScheme="brand"
                            variant="outline"
                            leftIcon={<AddIcon />}
                            onClick={onAssign}
                        >
                            {translate('coupon.add_users')}
                        </Button>
                    </HStack>
                </VStack>
            </SimpleGrid>
        </VStack>
    );
};

// ─── InfoRow Component ────────────────────────────────────────────

interface InfoRowProps {
    label: string;
    value: string;
    valueColor?: string;
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value, valueColor }) => (
    <HStack>
        <Text fontWeight="bold" minWidth="120px">{label}:</Text>
        <Text color={valueColor}>{value}</Text>
    </HStack>
);
